import { useRef, useState } from 'react';
import type { Scenario } from '../types';
import type { VideoSettings } from '../VideoGenerationSettings';
import type { CastingActor, SeedancePresetActorItem } from '../../state/types';

type FrameMode = 'first_only' | 'first_and_last' | 'first_with_next';

interface OriginalScenarioInfo {
  title: string;
  summary: string;
  frameMode: string;
  tags: string[];
  videoModel: string;
  resolution: string;
  castingIds: string[];
  presetActorIds: string[];
  /** 制作進捗 (organization_material_workflow_stages.id)。null = 未設定 */
  workflowStageId: string | null;
}

interface UseScenarioInfoModalReturn {
  isOpen: boolean;
  open: (scenario: Scenario, videoSettings: VideoSettings) => void;
  close: () => void;
  /** 編集モードのキャンセル用: 最後に open したシナリオの値へ全編集 state を戻す。 */
  reset: () => void;
  editingScenarioTitle: string;
  setEditingScenarioTitle: React.Dispatch<React.SetStateAction<string>>;
  editingScenarioSummary: string;
  setEditingScenarioSummary: React.Dispatch<React.SetStateAction<string>>;
  editingFrameMode: FrameMode;
  setEditingFrameMode: React.Dispatch<React.SetStateAction<FrameMode>>;
  editingTags: string[];
  setEditingTags: React.Dispatch<React.SetStateAction<string[]>>;
  editingVideoModel: string;
  setEditingVideoModel: React.Dispatch<React.SetStateAction<string>>;
  editingResolution: string;
  setEditingResolution: React.Dispatch<React.SetStateAction<string>>;
  editingCastings: CastingActor[];
  setEditingCastings: React.Dispatch<React.SetStateAction<CastingActor[]>>;
  editingPresetActors: SeedancePresetActorItem[];
  setEditingPresetActors: React.Dispatch<React.SetStateAction<SeedancePresetActorItem[]>>;
  /** 制作進捗の編集値。null = 未設定。open() で初期化・reset() で元値へ戻る
   *  (docs/features/workflow-stage-take8-and-scenario-book-plan.md §3.4.3) */
  editingWorkflowStageId: string | null;
  setEditingWorkflowStageId: React.Dispatch<React.SetStateAction<string | null>>;
  originalScenarioInfo: OriginalScenarioInfo;
}

export function useScenarioInfoModal(): UseScenarioInfoModalReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [editingScenarioTitle, setEditingScenarioTitle] = useState('');
  const [editingScenarioSummary, setEditingScenarioSummary] = useState('');
  const [editingFrameMode, setEditingFrameMode] = useState<FrameMode>('first_only');
  const [editingTags, setEditingTags] = useState<string[]>([]);
  const [editingVideoModel, setEditingVideoModel] = useState('');
  const [editingResolution, setEditingResolution] = useState('');
  // AI俳優の差し替え編集 state。通常俳優 (castings) と Seedance preset は排他で、
  // どちらが有効かは editingVideoModel のモデルファミリーで決まる (表示側で導出)。
  const [editingCastings, setEditingCastings] = useState<CastingActor[]>([]);
  const [editingPresetActors, setEditingPresetActors] = useState<SeedancePresetActorItem[]>([]);
  const [editingWorkflowStageId, setEditingWorkflowStageId] = useState<string | null>(null);
  const [originalScenarioInfo, setOriginalScenarioInfo] = useState<OriginalScenarioInfo>({
    title: '',
    summary: '',
    frameMode: 'first_only',
    tags: [],
    videoModel: 'veo-3.1',
    resolution: '1080',
    castingIds: [],
    presetActorIds: [],
    workflowStageId: null,
  });

  // 編集キャンセル時の巻き戻し用に、最後に open した引数を保持する。open() の
  // 初期化ロジックをそのまま再実行することで、編集 state の初期値導出 (frame_mode
  // フォールバック等) を二重実装しない。
  const lastOpenArgs = useRef<{ scenario: Scenario; videoSettings: VideoSettings } | null>(null);

  const open = (scenario: Scenario, videoSettings: VideoSettings) => {
    lastOpenArgs.current = { scenario, videoSettings };
    setEditingScenarioTitle(scenario.title || '');
    setEditingScenarioSummary(scenario.summary || '');
    setEditingFrameMode(
      scenario.frame_mode && scenario.frame_mode !== 'reference'
        ? (scenario.frame_mode as FrameMode)
        : 'first_only',
    );
    setEditingTags([...(scenario.tags || [])]);
    const effectiveVideoModel = (scenario.video_model || videoSettings.videoModel) as string;
    // output_resolution 未保存のレガシーシナリオは書き出し側フォールバック (1080p) と
    // 揃える。localStorage の videoSettings.resolution に引っ張られると、モーダル上は
    // 720p に見えるのに DB は NULL のまま → Premiere では 1080p が出る、という表示と
    // 書き出しの齟齬が発生するため。
    const effectiveResolution = (scenario.output_resolution || '1080') as string;
    setEditingVideoModel(effectiveVideoModel);
    setEditingResolution(effectiveResolution);

    // AI俳優: casting_models[] を正とし、単数 casting は旧データ用 fallback
    // (useScenarioGeneration の復元ロジックと同じ規則)。
    const castingList = (scenario.casting_models && scenario.casting_models.length > 0)
      ? scenario.casting_models
      : (scenario.casting ? [scenario.casting] : []);
    const castings: CastingActor[] = castingList.map((c) => ({
      id: c.id,
      display_name: c.display_name,
      face_image_public_url: c.face_image_public_url,
      character_sheet_public_url: c.character_sheet_public_url,
      gender: c.gender,
      min_age: c.min_age,
      max_age: c.max_age,
      ethnicity: c.ethnicity,
    }));
    const presetList = (scenario.seedance_preset_actors && scenario.seedance_preset_actors.length > 0)
      ? scenario.seedance_preset_actors
      : (scenario.seedance_preset_actor ? [scenario.seedance_preset_actor] : []);
    const presetActors: SeedancePresetActorItem[] = presetList.map((a) => ({
      id: a.id,
      byteplus_asset_id: a.byteplus_asset_id,
      display_name: a.display_name,
      thumbnail_public_url: a.thumbnail_public_url ?? undefined,
      gender: a.gender ?? undefined,
      min_age: a.min_age ?? undefined,
      max_age: a.max_age ?? undefined,
      ethnicity: a.ethnicity ?? undefined,
    }));
    setEditingCastings(castings);
    setEditingPresetActors(presetActors);
    const workflowStageId = scenario.workflow_stage_id ?? null;
    setEditingWorkflowStageId(workflowStageId);

    setOriginalScenarioInfo({
      title: scenario.title || '',
      summary: scenario.summary || '',
      frameMode: scenario.frame_mode || 'first_only',
      tags: [...(scenario.tags || [])],
      videoModel: effectiveVideoModel,
      resolution: effectiveResolution,
      castingIds: castings.map((c) => c.id),
      presetActorIds: presetActors.map((a) => a.id),
      workflowStageId,
    });
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const reset = () => {
    if (lastOpenArgs.current) {
      open(lastOpenArgs.current.scenario, lastOpenArgs.current.videoSettings);
    }
  };

  return {
    isOpen,
    open,
    close,
    reset,
    editingScenarioTitle,
    setEditingScenarioTitle,
    editingScenarioSummary,
    setEditingScenarioSummary,
    editingFrameMode,
    setEditingFrameMode,
    editingTags,
    setEditingTags,
    editingVideoModel,
    setEditingVideoModel,
    editingResolution,
    setEditingResolution,
    editingCastings,
    setEditingCastings,
    editingPresetActors,
    setEditingPresetActors,
    editingWorkflowStageId,
    setEditingWorkflowStageId,
    originalScenarioInfo,
  };
}

export type { UseScenarioInfoModalReturn };
